<template>
  <toggle-section
    section="corporateFiling"
    chevron-position="right"
    data-cy="corporate-filing-section"
    :is-open="isOpen"
    content-gray
    @toggle="isOpen = !isOpen"
  >
    <template #titleSection>
      <div class="flex items-center space-x-4">
        <lf-h4>{{ $t("DEALS.CLEAR_KYB.CORPORATE_FILING", 2) }}</lf-h4>
        <lf-pill :color-index="15">
          {{ data.length }}
        </lf-pill>
      </div>
    </template>
    <template #content>
      <div
        v-for="(filing, index) in data"
        :key="filing.SourceDocumentGuid"
        class="space-y-4 divide-y bg-white p-5"
      >
        <span class="text-gray-600">
          {{ $t("DEALS.CLEAR_KYB.CORPORATE_FILING") }} {{ index + 1 }}
        </span>
        <div class="flex flex-col space-y-1 pt-3">
          <div
            v-for="row in dataRows[index]"
            :key="row.label"
            class="row"
            :class="{ 'border-b': row.subtitle }"
          >
            <span :class="row.class ?? 'text-label'">{{ row.label }}</span>
            <span v-if="!row.subtitle" class="col-span-2 text-headline">
              {{ validateString(row.value) }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </toggle-section>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { validateString } from "@/helpers/common";
import { formatAddress } from "@/helpers/formatting";

import ToggleSection from "@/components/ToggleSection.vue";
import LfH4 from "@/components/ui/text/LfHeading4.vue";
import LfPill from "@/components/ui/text/LfPill.vue";

import type { CorporateFilingItem } from "@/models/commercialData/clear/ClearIdConfirmBusiness";

type DataRow = {
  label: string;
  value?: string;
  class?: string;
  subtitle?: boolean;
};

const { data } = defineProps<{
  data: CorporateFilingItem[];
}>();

const isOpen = ref(true);

const { t } = useI18n();

const dataRows = computed(() =>
  data.reduce((result, item) => {
    const acc: DataRow[] = [
      {
        label: t("COMMON.NOTE"),
        value: item.Note,
      },
      {
        label: t("DEALS.CLEAR_KYB.SOURCE"),
        value: item.Source,
      },
      {
        label: t("DEALS.CLEAR_KYB.CORP_NUMBER"),
        value: item.CorpNumber,
      },
      {
        label: t("SIDEBAR.EXPERIAN.BUSINESS_FACTS.BUSINESS_TYPE"),
        value: item.BusinessType,
      },
      {
        label: t("DEALS.CLEAR_KYB.CORP_LIFE_TIME"),
        value: item.CorpLifetime,
      },
      {
        label: t("DEALS.CLEAR_KYB.EFFECTIVE_DATE"),
        value: item.EffectiveDate,
      },
      {
        label: t("DEALS.CLEAR_KYB.FILING_DUNS_NUMBER"),
        value: item.FilingDUNSNumber,
      },
      {
        label: t("DEALS.CLEAR_KYB.SOURCE_DOCUMENT_GUID"),
        value: item.SourceDocumentGuid,
      },
      {
        label: t("BUSINESS_PROFILES.BUSINESS_INFO"),
        class: "text-gray-600 mt-2",
        subtitle: true,
      },
      {
        label: t("DEALS.CLEAR_KYB.DUNS_NUMBER"),
        value: item.BusinessInfo?.DUNSNumber,
      },
      {
        label: t("DEALS.CLEAR_KYB.FILING_DATE"),
        value: item.BusinessInfo?.FilingDate,
      },
      {
        label: t("DEALS.CLEAR_KYB.FILING_STATE"),
        value: item.BusinessInfo?.FilingState,
      },
      {
        label: t("DEALS.CLEAR_KYB.BUSINESS_NAME"),
        value: item.BusinessInfo?.BusinessName,
      },
      {
        label: t("DEALS.CLEAR_KYB.CORP_BUSINESS_STATUS"),
        value: item.BusinessInfo?.CorpBusinessStatus,
      },
      {
        label: t("DEALS.CLEAR_KYB.FILING_OFFICE_ADDRESS"),
        value: formatAddress({
          address_line: item.BusinessInfo?.FilingOfficeAddress?.Address.Street,
          city: item.BusinessInfo?.FilingOfficeAddress?.Address.City,
          state: item.BusinessInfo?.FilingOfficeAddress?.Address.State,
          zip: item.BusinessInfo?.FilingOfficeAddress?.Address.ZipCode,
        }),
      },
      {
        label: t("DEALS.CLEAR_KYB.FILING_STATE"),
        value: item.BusinessInfo?.FilingOfficeAddress?.FilingState,
      },
      {
        label: t("DEALS.CLEAR_KYB.FILING_OFFICE"),
        value: item.BusinessInfo?.FilingOfficeAddress?.FilingOfficeName,
      },
      {
        label: t("DEALS.CLEAR_KYB.STATE_OF_INCORPORATION"),
        value: item.BusinessInfo?.StateOfIncorporation,
      },
      {
        label: t("DEALS.CLEAR_KYB.CORP_AGENT_INFO"),
        class: "text-gray-600 mt-2",
        subtitle: true,
      },
      {
        label: t("DEALS.CLEAR_KYB.CORP_AGENT_FULL_NAME"),
        value: item.CorpAgentInfo?.PersonName?.FullName,
      },
      {
        label: t("DEALS.CLEAR_KYB.CORP_AGENT_ADDRESS"),
        value: formatAddress({
          address_line: item?.CorpAgentInfo?.CorpAgentAddress?.StreetLine1,
          city: item?.CorpAgentInfo?.CorpAgentAddress?.City,
          state: item?.CorpAgentInfo?.CorpAgentAddress?.State,
          zip: item?.CorpAgentInfo?.CorpAgentAddress?.ZipCode,
        }),
      },
      {
        label: t("DEALS.CLEAR_KYB.CORP_OFFICER_INFO"),
        class: "text-gray-600 mt-2",
        subtitle: true,
      },
      {
        label: t("DEALS.CLEAR_KYB.CORP_OFFICER_FULL_NAME"),
        value: item.CorpOfficerInfo?.PersonName?.FullName,
      },
      {
        label: t("DEALS.CLEAR_KYB.CORP_OFFICER_ADDRESS"),
        value: formatAddress({
          address_line: item?.CorpOfficerInfo?.CorpOfficerAddress?.StreetLine1,
          city: item?.CorpOfficerInfo?.CorpOfficerAddress?.City,
          state: item?.CorpOfficerInfo?.CorpOfficerAddress?.State,
          zip: item?.CorpOfficerInfo?.CorpOfficerAddress?.ZipCode,
        }),
      },
      {
        label: t("DEALS.CLEAR_KYB.NAME_TYPE"),
        value: item.CorpOfficerInfo?.NameTypeDesc,
      },
    ];

    result.push(acc);
    return result;
  }, [] as DataRow[][])
);
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.row {
  @apply grid grid-cols-3;
}
</style>
